/**
 * useTags Hooks
 *
 * React Query hooks for tag operations
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';

import { tagsService, investmentTagsService } from '../services/tags.service';
import { unwrapResponse } from '../types';

import { investmentKeys } from './useInvestments';

import type {
  Tag,
  TagWithCount,
  InvestmentTag,
  AddTagDto,
  AddTagResponse,
  RemoveTagResponse,
  TagsListResponse,
  InvestmentTagsResponse,
} from '../services/tags.service';

/**
 * Query keys for tags
 */
export const tagKeys = {
  all: ['tags'] as const,
  list: (search?: string) => [...tagKeys.all, 'list', search] as const,
  investmentTags: (investmentId: number) => [...tagKeys.all, 'investment', investmentId] as const,
};

/**
 * Fetch all tags with optional search filter
 */
export function useTags(
  search?: string,
  options?: Omit<UseQueryOptions<TagWithCount[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<TagWithCount[], Error>({
    queryKey: tagKeys.list(search),
    queryFn: async () => {
      const response = await tagsService.getAll(search);
      const data = unwrapResponse<{ tags?: TagWithCount[] }>(response.data);
      return data.tags || [];
    },
    ...options,
  });
}

/**
 * Fetch tags for a specific investment
 */
export function useInvestmentTags(
  investmentId: number,
  options?: Omit<UseQueryOptions<InvestmentTag[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<InvestmentTag[], Error>({
    queryKey: tagKeys.investmentTags(investmentId),
    queryFn: async () => {
      const response = await investmentTagsService.getByInvestmentId(investmentId);
      const data = unwrapResponse<{ tags?: InvestmentTag[] }>(response.data);
      return data.tags || [];
    },
    enabled: investmentId > 0,
    ...options,
  });
}

/**
 * Add a tag to an investment
 */
export function useAddTag(
  options?: Omit<
    UseMutationOptions<AddTagResponse, Error, { investmentId: number; data: AddTagDto }>,
    'mutationFn'
  >
) {
  const queryClient = useQueryClient();

  return useMutation<AddTagResponse, Error, { investmentId: number; data: AddTagDto }>({
    mutationFn: async ({ investmentId, data }) => {
      const response = await investmentTagsService.addTag(investmentId, data);
      return unwrapResponse<AddTagResponse>(response.data);
    },
    onSuccess: (_, { investmentId }) => {
      // Invalidate investment tags
      queryClient.invalidateQueries({ queryKey: tagKeys.investmentTags(investmentId) });
      // Invalidate global tags list (counts may have changed)
      queryClient.invalidateQueries({ queryKey: tagKeys.all });
      // Invalidate investments list (tags are included in the response)
      queryClient.invalidateQueries({ queryKey: investmentKeys.all });
    },
    ...options,
  });
}

/**
 * Remove a tag from an investment
 */
export function useRemoveTag(
  options?: Omit<
    UseMutationOptions<RemoveTagResponse, Error, { investmentId: number; tagId: number }>,
    'mutationFn'
  >
) {
  const queryClient = useQueryClient();

  return useMutation<RemoveTagResponse, Error, { investmentId: number; tagId: number }>({
    mutationFn: async ({ investmentId, tagId }) => {
      const response = await investmentTagsService.removeTag(investmentId, tagId);
      return unwrapResponse<RemoveTagResponse>(response.data);
    },
    onSuccess: (_, { investmentId }) => {
      // Invalidate investment tags
      queryClient.invalidateQueries({ queryKey: tagKeys.investmentTags(investmentId) });
      // Invalidate global tags list (counts may have changed, tag may have been deleted)
      queryClient.invalidateQueries({ queryKey: tagKeys.all });
      // Invalidate investments list (tags are included in the response)
      queryClient.invalidateQueries({ queryKey: investmentKeys.all });
    },
    ...options,
  });
}

// Re-export types for convenience
export type {
  Tag,
  TagWithCount,
  InvestmentTag,
  AddTagDto,
  AddTagResponse,
  RemoveTagResponse,
  TagsListResponse,
  InvestmentTagsResponse,
};
